/**
 * Les dates et les heures, toujours celles de Paris.
 *
 * `toLocaleDateString('fr-FR')` sans plus de précision formate dans le fuseau
 * du navigateur. Pour un bureau assis en France cela ne se voit pas ; pour un
 * trésorier en déplacement, un bénévole expatrié ou simplement un téléphone
 * mal réglé, un créneau de 19h00 s'affiche 18h00 ou 20h00 — et l'on se
 * présente à la mauvaise heure sans que rien n'ait l'air faux.
 *
 * La règle est donc unique : l'association vit à l'heure de Paris, et ses
 * écrans l'écrivent en heure de Paris, quel que soit l'endroit d'où on les
 * regarde. Le format est celui de 24 heures, sans « am » ni « pm ».
 */

const FUSEAU = 'Europe/Paris';

/** Ce qu'on accepte : une chaîne ISO, un horodatage, ou rien. */
type Instant = string | number | Date | null | undefined;

function versDate(valeur: Instant): Date | null {
  if (valeur === null || valeur === undefined || valeur === '') return null;
  const d = valeur instanceof Date ? valeur : new Date(valeur);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** « 19/08/2026 » */
export function dateFr(valeur: Instant, siVide = ''): string {
  const d = versDate(valeur);
  if (!d) return siVide;
  return d.toLocaleDateString('fr-FR', {
    timeZone: FUSEAU,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** « mardi 19 août 2026 » */
export function dateLongueFr(valeur: Instant, siVide = ''): string {
  const d = versDate(valeur);
  if (!d) return siVide;
  return d.toLocaleDateString('fr-FR', {
    timeZone: FUSEAU,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** « 13h17 » — jamais « 1:17 PM ». */
export function heureFr(valeur: Instant, siVide = ''): string {
  const d = versDate(valeur);
  if (!d) return siVide;
  return d
    .toLocaleTimeString('fr-FR', {
      timeZone: FUSEAU,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
    .replace(':', 'h');
}

/** « 19/08/2026 à 13h17 » */
export function dateHeureFr(valeur: Instant, siVide = ''): string {
  const d = versDate(valeur);
  if (!d) return siVide;
  return `${dateFr(d)} à ${heureFr(d)}`;
}

/** « mardi 19 août 2026 à 13h17 » */
export function dateLongueHeureFr(valeur: Instant, siVide = ''): string {
  const d = versDate(valeur);
  if (!d) return siVide;
  return `${dateLongueFr(d)} à ${heureFr(d)}`;
}

/** « 19 août » — pour les listes, où l'année va de soi. */
export function jourMoisFr(valeur: Instant, siVide = ''): string {
  const d = versDate(valeur);
  if (!d) return siVide;
  return d.toLocaleDateString('fr-FR', { timeZone: FUSEAU, day: 'numeric', month: 'long' });
}
